import React, { useState, useEffect } from 'react';

interface ShippingLoadingAnimationProps {
  visible: boolean;
}

const stages = [
  {
    title: 'Analyzing Your Shipment',
    subtitle: 'Processing dimensions, weight, and destination...',
    color: 'var(--brand-blue)',
    icon: '🔍',
  },
  {
    title: 'Comparing Carriers',
    subtitle: 'Checking rates from multiple shipping providers...',
    color: 'var(--brand-cyan)',
    icon: '🔄',
  },
  {
    title: 'Finding Best Options',
    subtitle: 'Selecting the most cost-effective solutions...',
    color: 'var(--ok)',
    icon: '✓',
  },
];

const ShippingLoadingAnimation: React.FC<ShippingLoadingAnimationProps> = ({ visible }) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!visible) return;

    const stageTimer = setInterval(() => {
      setCurrentStage((prev) => (prev + 1) % stages.length);
    }, 2500);

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 0;
        return prev + Math.random() * 15 + 5;
      });
    }, 1000);

    return () => {
      clearInterval(stageTimer);
      clearInterval(progressTimer);
    };
  }, [visible]);

  if (!visible) return null;

  const stage = stages[currentStage];
  const progressPct = Math.min(100, Math.round(progress));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 32px',
        width: '100%',
        minHeight: 400,
        borderRadius: 12,
        background: 'var(--bg-tint)',
        border: '1px solid var(--line)',
      }}
    >
      {/* Animated hub */}
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'var(--brand-blue)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
          marginBottom: 32,
          boxShadow: '0 8px 32px rgba(43,85,255,0.3)',
          animation: 'shippingPulse 2s ease-in-out infinite',
        }}
      >
        {stage.icon}
      </div>

      {/* Transport icons row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {['🚚', '✈️', '🚢'].map((icon, i) => (
          <div
            key={i}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'var(--bg-elev)',
              border: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              animation: `shipBounce${i} ${2 + i * 0.5}s ease-in-out infinite`,
            }}
          >
            {icon}
          </div>
        ))}
      </div>

      {/* Title + subtitle */}
      <h3
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: stage.color,
          marginBottom: 8,
          textAlign: 'center',
          transition: 'color 0.5s ease',
        }}
      >
        {stage.title}
      </h3>
      <p
        style={{
          fontSize: 14,
          color: 'var(--ink-3)',
          textAlign: 'center',
          marginBottom: 24,
          maxWidth: 360,
        }}
      >
        {stage.subtitle}
      </p>

      {/* Progress bar */}
      <div style={{ width: '100%', maxWidth: 400, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Progress</span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{progressPct}%</span>
        </div>
        <div
          style={{
            width: '100%',
            height: 8,
            borderRadius: 4,
            background: 'var(--line)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(90deg, var(--brand-blue), var(--brand-cyan))',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Stage dots */}
      <div style={{ display: 'flex', gap: 8 }}>
        {stages.map((_, i) => (
          <div
            key={i}
            style={{
              width: i === currentStage ? 20 : 8,
              height: 8,
              borderRadius: 4,
              background: i === currentStage ? 'var(--brand-blue)' : 'var(--line-strong)',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes shippingPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes shipBounce0 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shipBounce1 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes shipBounce2 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
};

export default ShippingLoadingAnimation;
